<template>
  <div class="organization-settings-permissions team-permissions">
    <div
      v-if="!validPlan"
      class="upgrade-alert alert alert-disabled p-20"
    >
      <div class="alert-content d-sm-flex align-items-center justify-content-between">
        <span class="d-flex align-items-center">
          <i class="hi hi-lock text-primary mr-15"></i>
          <span>
            You don’t have access to the
            <strong>Team permission feature</strong>. Upgrade to try it out today
          </span>
        </span>
        <span class="d-flex align-items-center justify-content-between flex-shrink-0 pl-5">
          <a
            href="https://support.hubstaff.com/hubstaff-user-rolepermissions-guide/"
            class="mr-20"
          >
            Learn more
          </a>
          <a
            :href="urls.upgradePremium"
            class="btn btn-primary btn-sm"
          >
            Upgrade plan
          </a>
        </span>
      </div>
    </div>

    <div
      class="text-grey mb-20"
      :class="{ 'text-superlight': !validPlan }"
    >
      <label class="text-uppercase small my-0">
        Team permission structure
        <i
          v-tooltip
          class="hi hi-info-filled"
          :data-original-title="tooltips.team_permission_structure"
        />
      </label>
      <p>
        When teams are assigned to projects, allow Team leads to view all members on the project including names,
        screenshots, activity, timesheets (even for members not on their team).
      </p>
    </div>
    <div
      v-if="validPlan"
      class="permissions-list"
    >
      <div class="form-group lead_to_project_manager text-grey">
        <label class="is-regular">Global</label>
        <div class="checkbox">
          <AppOnOffToggle
            :value="permissionsData.team_lead.lead_to_project_manager"
            @input="handleToggle"
          >
            <span>Allow Team leads to manage projects</span>
          </AppOnOffToggle>
        </div>
        <div
          v-if="exceptionsList.length"
          class="mt-20"
        >
          <span class="exceptions-warning dialog-warning orange d-flex align-items-center mb-5 mt-10 gap-5">
            <i class="hi hi-warning text-orange mr-5" />
            <span>
              <a
                v-tooltip="{ html: true }"
                href="javascript:void(0);"
                :data-original-title="exceptionsTooltip"
              >
                {{ exceptionsLinkLabel }}
              </a>
              {{ exceptionsLabel }}
            </span>
          </span>
        </div>
      </div>

      <hr />

      <div class="form-group batch-permissions">
        <div>
          <h4>General settings</h4>
          <p class="text-grey">Adjust all Team lead permissions with the general settings below</p>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th>Team lead permissions</th>
              <th>
                Teams
                <i
                  v-tooltip
                  class="hi hi-info-filled"
                  data-original-title="Teams where Team leads have the selected permissions ON"
                />
              </th>
              <th>Only notify Team leads</th>
            </tr>
          </thead>
          <tbody class="text-grey">
            <tr
              v-for="ability in leadAbilities"
              :key="ability.key"
              :data-testid="ability.key"
            >
              <td>
                <label class="custom-checkbox-wrapper blue-checkbox d-inline-flex is-regular">
                  <input
                    type="checkbox"
                    :checked="ability.enabled"
                    :name="ability.key"
                    @click="(ev) => handleAbilityToggle(ev, ability)"
                  />
                  <span class="custom-checkbox-box"></span>
                  {{ ability.label }}

                  <i
                    v-if="ability.warnings?.length"
                    v-tooltip="{ html: true }"
                    :data-original-title="abilityWarnings(ability.warnings)"
                    class="hi hi-info-filled warnings-tooltip ml-5"
                  />
                </label>
                <div
                  v-if="abilityExceptions(ability).length"
                  class="ml-20"
                >
                  <span
                    v-tooltip="{ html: true }"
                    :data-original-title="abilityExceptionsTooltip(ability)"
                    class="text exception-label border-radius"
                  >
                    <i class="hi hi-info-filled" />
                    {{ abilityExceptionsLabel(ability) }}
                  </span>
                </div>
                <div
                  v-if="customNotificationsLabel(ability)"
                  class="ml-20 mt-5"
                >
                  <AppOnOffToggle
                    :disabled="!ability.enabled"
                    :value="ability.notification.enabled"
                    class="is-regular"
                    @input="(_v, ev) => updateTeamNotification(ability, ev.target.checked)"
                  >
                    {{ customNotificationsLabel(ability) }}
                  </AppOnOffToggle>
                </div>
              </td>
              <td>
                <div class="d-flex align-items-center notif-switcher">
                  <AppAvatarInitial
                    v-for="team in abilityEnabledTeams(ability).slice(0, 10)"
                    :key="`${ability.key}-${team.id}`"
                    v-tooltip
                    :text="team.name"
                    class="avatar avatar-overlaps"
                    :title="team.name"
                  />
                  <span
                    v-if="moreAbilityEnabledTeamsQty(ability)"
                    class="more-members-badge light"
                  >
                    +{{ moreAbilityEnabledTeamsQty(ability) }}
                  </span>
                </div>
              </td>
              <td>
                <AppOnOffToggle
                  v-if="ability.notification && !customNotificationsLabel(ability)"
                  :disabled="!ability.enabled"
                  :value="ability.notification.enabled"
                  @input="(_v, ev) => updateTeamNotification(ability, ev.target.checked)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <hr />
    </div>
    <AppDialog
      v-if="visibleConfirm"
      show
      :title="confirmTitle"
      append-to-body
      class="teams-confirm-dialog"
      @close="closeConfirm"
    >
      {{ confirmDescr }}
      <div class="mt-10">
        <div class="form-option alert-basic alert">
          <label class="d-flex align-items-start mb-0 confirm-dialog-label">
            <div class="radio-button-wraper pl-5">
              <input
                v-model="changeMode"
                type="radio"
                value="retroactive"
                class="form-check-input"
              />
            </div>
            <span class="d-inline-block ml-25">
              Team leads on all projects
              <span
                class="text-grey is-regular d-inline-block mt-5"
                v-html="confirmOption1Descr"
              ></span>
            </span>
          </label>
        </div>
        <div class="form-option alert-basic alert">
          <label class="d-flex align-items-start mb-0 confirm-dialog-label">
            <div class="radio-button-wraper pl-5">
              <input
                v-model="changeMode"
                type="radio"
                value="no_retroactive"
                class="form-check-input"
              />
            </div>
            <span class="d-inline-block ml-25">
              Teams leads assigned to future projects
              <span
                class="text-grey is-regular d-inline-block mt-5"
                v-html="confirmOption2Descr"
              ></span>
            </span>
          </label>
        </div>
      </div>
      <template #footer>
        <div class="justify-content-end">
          <AppButton
            type="default"
            @click="closeConfirm"
          >
            Cancel
          </AppButton>
          <AppButton
            type="primary"
            @click="updateSetting('team_lead', 'lead_to_project_manager')"
          >
            Confirm
          </AppButton>
        </div>
      </template>
    </AppDialog>

    <AppDialog
      v-if="confirmAbilityFor"
      show
      :title="confirmAbilityTitle"
      append-to-body
      class="ability-confirm-dialog"
      @close="closeConfirm"
    >
      {{ confirmAbilityBody }}

      <template #footer>
        <div class="justify-content-end">
          <AppButton
            type="default"
            @click="closeConfirm"
          >
            Cancel
          </AppButton>
          <AppButton
            type="primary"
            @click="updateTeamAbility(confirmAbilityFor.key, !confirmAbilityFor.enabled)"
          >
            Confirm
          </AppButton>
        </div>
      </template>
    </AppDialog>
  </div>
</template>

<script>
import RolePermissionMixin from '~/mixins/RolePermissionMixin';
import { OrganizationsResource } from '~/resources';
import { cloneDeep } from 'lodash-es';
import { pluralize } from '~/utils/string_utils';

export default {
  mixins: [RolePermissionMixin],
  props: {
    currentPermissions: {
      // {team_lead: ['lead_to_project_manager', ..], ...}
      type: Object,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    validPlan: {
      type: Boolean,
      default: true,
    },
    exceptions: {
      type: Array,
      default: () => [],
    },
    teamsSettings: {
      type: Object,
      default: () => ({ permissions: [], teams: [] }),
      // Sample: {permissions: [{key: 'edit_org_permissions', label: 'Edit organization permissions', enabled: true,
      //          teams: [{id: 1, name: 'test 1', permissions: {edit_org_permissions: true, ...}, ...]}]}
    },
  },
  data() {
    return {
      permissionsData: cloneDeep(this.currentPermissions),
      tooltips: Object.freeze({
        team_permission_structure: 'Control Team lead permissions, when added to projects through their teams',
        lead_to_project_manager: 'This is what all current and future teams will be set to',
      }),
      urls: Object.freeze({
        upgradePremium: OrganizationsResource.plansLink({ id: this.organizationId }),
      }),
      visibleConfirm: false,
      changeMode: 'retroactive',
      exceptionsList: this.exceptions,
      confirmAbilityFor: null,
      leadAbilities: cloneDeep(this.teamsSettings.permissions),
      teams: cloneDeep(this.teamsSettings.teams),
    };
  },
  computed: {
    exceptionsLabel() {
      return `${pluralize(
        this.exceptionsList.length,
        'setting does not match',
        'settings do not match',
      )} the org default settings.
      You can change individual Team lead settings by changing the Project role on the individual Teams.`;
    },
    exceptionsTooltip() {
      const visibleNames = this.exceptionsList.slice(0, 10);
      const hiddenNames = this.exceptionsList.slice(10);

      return `<div class="text-left">
          <ul class="pl-20 mb-0">${visibleNames.map((item) => `<li>${item.name}</li>`).join('')}</ul>
          ${hiddenNames.length ? `<div>+ ${hiddenNames.length} ${pluralize(hiddenNames.length, 'member')}</div>` : ''}
        </div>`;
    },
    exceptionsLinkLabel() {
      return `${this.exceptionsList.length} ${pluralize(this.exceptionsList.length, 'Team Lead', 'Team Leads')}`;
    },
    confirmTitle() {
      const on = 'Confirm changes: Don’t allow Team leads to manage projects';
      const off = 'Confirm changes: Allow Team leads to manage projects';

      return this.permissionsData.team_lead.lead_to_project_manager ? on : off;
    },
    confirmDescr() {
      const on =
        'Turning OFF this global setting will remove permission for Team leads to manage projects that their teams are assigned. Apply this change to:';
      const off =
        'Turning ON this global setting will allow Team leads to manage projects that their teams are assigned. Apply this change to:';

      return this.permissionsData.team_lead.lead_to_project_manager ? on : off;
    },
    confirmOption1Descr() {
      const on =
        'Team leads, including those already assigned to existing project <b>will no longer be able to manage projects</b>. ';
      const off =
        'Team leads, including those already assigned to existing projects <b>will be able to manage projects</b>.';

      return this.permissionsData.team_lead.lead_to_project_manager ? on : off;
    },
    confirmOption2Descr() {
      const on =
        'Teams leads assigned to future projects <b>will no longer be able to manage projects</b>. Team leads already assigned to existing projects will remain Project managers and this can be changed later on individual Team leads profiles. ';
      const off =
        'Team leads assigned to future projects <b>will be able to manage projects</b>. This won’t change existing Team lead roles on projects they are already assigned and this can be changed later on individual Team lead profiles.';

      return this.permissionsData.team_lead.lead_to_project_manager ? on : off;
    },
    confirmAbilityTitle() {
      return `Confirm changes - Turn ${this.confirmAbilityFor.enabled ? 'OFF' : 'ON'} ${
        this.confirmAbilityFor.label
      } setting?`;
    },
    confirmAbilityBody() {
      return `You’re about to change "${this.confirmAbilityFor.label}" setting for all Team leads. Do you want to proceed with changing this permission?`;
    },
  },
  methods: {
    updateSetting(role, permissionKey) {
      const flag = !this.permissionsData[role][permissionKey];

      this.callSaveSetting(role, permissionKey, flag, this.changeMode === 'retroactive', (response) => {
        this.permissionsData[role][permissionKey] = flag;
        this.exceptionsList = response.exceptions || [];
      });
    },
    callSaveSetting(roleKey, permissionKey, flag, retroactive, callback) {
      this.saveSetting(roleKey, permissionKey, flag, retroactive)
        .then((response) => {
          callback(response);
          this.$appMessage('Setting saved!');
          this.closeConfirm();
        })
        .catch((msg) => {
          this.$appMessage(msg);
        });
    },
    closeConfirm() {
      this.visibleConfirm = false;
      this.confirmAbilityFor = null;
    },
    handleToggle(value, event) {
      if (event) {
        event.preventDefault();
      }

      this.visibleConfirm = true;
    },

    customNotificationsLabel(ability) {
      return ability.key === 'lead_team_management' ? 'Invite members to the organization' : null;
    },
    abilityExceptions(ability) {
      return this.teams.filter((team) => team.permissions[ability.key] !== ability.enabled);
    },
    abilityExceptionsLabel(ability) {
      const qtyExceptions = this.abilityExceptions(ability).length;

      return `${qtyExceptions} ${pluralize(qtyExceptions, 'exception')}`;
    },
    abilityExceptionsTooltip(ability) {
      const exceptionTeams = this.abilityExceptions(ability);
      const visibleTeams = exceptionTeams.slice(0, 10);
      const prefix = `${pluralize(exceptionTeams.length, 'This team has', 'These teams have')} `;
      const qtyHiddenTeams = exceptionTeams.slice(10).length;

      return `<div class="text-left">
        <div>${prefix} ${ability.label} <b>${ability.enabled ? 'OFF' : 'ON'}</b> </div>
        <ul class="pl-20 mb-0">
          ${visibleTeams.map((team) => `<li>${team.name}</li>`).join('')}
        </ul>
        ${qtyHiddenTeams ? `<div>+${qtyHiddenTeams} ${pluralize(qtyHiddenTeams, 'team')}</div>` : ''}
      </div>`;
    },
    abilityEnabledTeams(ability) {
      return this.teams.filter((team) => team.permissions[ability.key] === true);
    },
    moreAbilityEnabledTeamsQty(ability) {
      return this.abilityEnabledTeams(ability).slice(10).length;
    },
    handleAbilityToggle(event, ability) {
      event.preventDefault();
      this.confirmAbilityFor = ability;
    },
    abilityWarnings(warnings) {
      return `<ul class='aligned-list text-left'>${warnings.map((warning) => `<li>${warning}</li>`).join('')}</ul>`;
    },
    updateTeamAbility(abilityKey, enabled) {
      const ability = this.leadAbilities.find((p) => p.key === abilityKey);

      this.callSaveSetting('team', ability.key, enabled, true, () => {
        ability.enabled = enabled;
        if (!enabled && ability.notification) {
          ability.notification.enabled = enabled;
        }

        if (enabled && ability.notification && ability.notification?.default_setting === true) {
          ability.notification.enabled = enabled;
        }

        this.teams.forEach((team) => {
          team.permissions[ability.key] = enabled;
        });
        this.closeConfirm();
      });
    },
    updateTeamNotification(ability, enabled) {
      this.callSaveSetting('team', ability.notification.key, enabled, true, () => {
        ability.notification.enabled = enabled;
        this.closeConfirm();
      });
    },
  },
};
</script>
